/**
 * Notification Service
 *
 * Production-ready notification service with comprehensive error handling,
 * performance optimizations, and robust caching strategies.
 *
 * Features:
 * - Intelligent caching with automatic cleanup
 * - Comprehensive error handling with retry mechanisms
 * - Rate limiting and duplicate prevention
 * - Performance monitoring and logging
 * - Production-ready error boundaries
 *
 * @version 2.0.0
 * @author Notification System Team
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  getDoc,
  onSnapshot,
  serverTimestamp,
  type Timestamp,
  type Unsubscribe,
  type QueryDocumentSnapshot
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, app } from '@/lib/firebase';
import { COLLECTION_NAMES } from '@/lib/collections';
import { log } from '@/lib/logger';


// Firestore notification document structure
export interface NotificationDocument {
  id?: string;
  type: 'mention' | 'system' | 'workflow';
  recipientId: string; // system_users document ID
  status: 'unread' | 'read' | 'dismissed';
  
  // Mention-specific data
  mentionData?: {
    messageId: string;
    channelId: string;
    workflowId: string;
    entryId: string;
    mentionedBy: string;
    messageText: string;
    messageTimestamp: Timestamp;
    // Deep link fields for cross-domain comments
    pageUrl?: string;      // e.g., "/intel/overview"
    scrollY?: number;      // Scroll position for precise navigation
    pinId?: string;        // Comment pin ID for highlighting
  };
  
  // Pre-computed display data
  title: string;
  description: string;
  entryDisplayName: string; // "Facebook Social Autocad"
  
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// Mention data for creating notifications
export interface MentionData {
  messageId: string;
  channelId: string;
  workflowId: string;
  entryId: string;
  mentionedBy: string;
  messageText: string;
  messageTimestamp: Date;
  workflowName?: string;
  entryDescription?: string;
  // Deep link fields for cross-domain comments
  pageUrl?: string;      // e.g., "/intel/overview"
  scrollY?: number;      // Scroll position for precise navigation
  pinId?: string;        // Comment pin ID for highlighting
}

// Pagination options for notification queries
interface NotificationPaginationOptions {
  limitCount?: number;
  lastDoc?: QueryDocumentSnapshot;
}

/**
 * Production-ready NotificationService with comprehensive error handling,
 * performance optimizations, and monitoring capabilities.
 */
export class NotificationService {
  private static readonly NOTIFICATIONS_COLLECTION = 'notifications';

  /**
   * Create a mention notification from chat message data
   */
  static async createMentionNotification(
    recipientId: string,
    mentionData: MentionData
  ): Promise<void> {
    try {
      
      // Check for duplicate notifications within the last 5 minutes
      const recentTimeThreshold = new Date(Date.now() - 5 * 60 * 1000); // 5 minutes ago
      const duplicateQuery = query(
        collection(db, this.NOTIFICATIONS_COLLECTION),
        where('recipientId', '==', recipientId),
        where('type', '==', 'mention'),
        where('mentionData.messageId', '==', mentionData.messageId),
        where('createdAt', '>', recentTimeThreshold)
      );
      
      const duplicateSnapshot = await getDocs(duplicateQuery);
      
      if (!duplicateSnapshot.empty) {
        return;
      }

      // Get mentioner's profile for display
      // mentionData.mentionedBy is the employee_id, but we need to use it as document ID
      let mentionerName = `User ${mentionData.mentionedBy}`;
      
      try {
        // Get user document directly by document ID (which matches employee_id)
        const userDocRef = doc(db, COLLECTION_NAMES.APP_USERS, mentionData.mentionedBy);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          mentionerName = userData.employee_data?.name || userData.name || `User ${mentionData.mentionedBy}`;
        } else {
          log.warn(`[NOTIFICATION-SERVICE] No system_users document found for ID: ${mentionData.mentionedBy}`);
        }
      } catch (error) {
        log.error(`Error fetching mentioner profile for ID ${mentionData.mentionedBy}:`, error);
      }

      // Get actual workflow name from workflow_configs collection
      let actualWorkflowName = mentionData.workflowName || 'Unknown Workflow';
      
      try {
        if (mentionData.workflowId) {
          // Try workflow_configs first
          const workflowConfigRef = doc(db, COLLECTION_NAMES.WORKFLOW_CONFIGS, mentionData.workflowId);
          const workflowConfigDoc = await getDoc(workflowConfigRef);
          
          if (workflowConfigDoc.exists()) {
            const workflowData = workflowConfigDoc.data();
            actualWorkflowName = workflowData.workflowName || workflowData.name || actualWorkflowName;
          } else {
            // Try workflows collection as fallback
            const workflowRef = doc(db, 'workflows', mentionData.workflowId);
            const workflowDoc = await getDoc(workflowRef);
            
            if (workflowDoc.exists()) {
              const workflowData = workflowDoc.data();
              actualWorkflowName = workflowData.workflowName || workflowData.name || actualWorkflowName;
            }
          }
        }
      } catch (error) {
        log.error(`Error fetching workflow name for ID ${mentionData.workflowId}:`, error);
      }

      // Pre-compute display strings with better formatting
      const title = "You have a new mention";
      const description = `@${mentionerName} mentioned you in: "${this.truncateText(mentionData.messageText, 100)}"`;
      
      // Create a comprehensive entry display: "Workflow - Channel - Tactic - Description"
      // Parse the entryDescription to extract channel, tactic, description
      const entryParts = mentionData.entryDescription ? mentionData.entryDescription.split(' - ') : [];
      const entryDisplayName = [actualWorkflowName, ...entryParts].filter(Boolean).join(' - ') || mentionData.entryId;

      const notificationDoc: Omit<NotificationDocument, 'id'> = {
        type: 'mention',
        recipientId,
        status: 'unread',
        mentionData: {
          messageId: mentionData.messageId,
          channelId: mentionData.channelId,
          workflowId: mentionData.workflowId,
          entryId: mentionData.entryId,
          mentionedBy: mentionData.mentionedBy,
          messageText: mentionData.messageText,
          messageTimestamp: serverTimestamp() as Timestamp,
          // Include deep link fields if provided (for cross-domain comments)
          ...(mentionData.pageUrl && { pageUrl: mentionData.pageUrl }),
          ...(mentionData.scrollY !== undefined && { scrollY: mentionData.scrollY }),
          ...(mentionData.pinId && { pinId: mentionData.pinId }),
        },
        title,
        description,
        entryDisplayName,
        createdAt: serverTimestamp() as Timestamp,
        updatedAt: serverTimestamp() as Timestamp,
      };

      await addDoc(collection(db, this.NOTIFICATIONS_COLLECTION), notificationDoc);

      // Send Slack notification asynchronously (don't block on failure)
      this.sendSlackNotification(recipientId, mentionData, entryDisplayName)
        .catch(err => {
          log.error(`[NOTIFICATION-SERVICE] Failed to send Slack notification to user ${recipientId}:`, err);
          // Don't throw - in-app notification was created successfully
        });

    } catch (error) {
      log.error('Error creating mention notification:', error);
      throw error;
    }
  }

  /**
   * Send Slack notification to a user about a mention
   * This is called automatically when a mention notification is created
   * Failures are logged but don't throw to avoid breaking the notification flow
   */
  private static async sendSlackNotification(
    recipientId: string,
    mentionData: MentionData,
    entryDisplayName: string
  ): Promise<void> {
    try {

      // Get recipient's Slack user ID from system_users collection
      const userDocRef = doc(db, COLLECTION_NAMES.APP_USERS, recipientId);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        log.warn(`[NOTIFICATION-SERVICE] User document not found for Slack notification: ${recipientId}`);
        return;
      }

      const userData = userDoc.data();
      const slackUserId = userData?.slack_user_id;

      if (!slackUserId) {
        return;
      }

      // Get mentioner's name for the message
      let mentionerName = 'Someone';
      try {
        const mentionerDocRef = doc(db, COLLECTION_NAMES.APP_USERS, mentionData.mentionedBy);
        const mentionerDoc = await getDoc(mentionerDocRef);
        if (mentionerDoc.exists()) {
          const mentionerData = mentionerDoc.data();
          mentionerName = mentionerData.employee_data?.name || mentionerData.name || 'Someone';
        }
      } catch (error) {
        log.warn(`[NOTIFICATION-SERVICE] Could not fetch mentioner name for Slack message:`, error);
      }

      // Extract domain name only (matching in-app notification format)
      let domainName = 'Workspace';
      if (mentionData.pageUrl) {
        // Extract just the domain from URL like "/intel/overview" -> "Intel"
        const pathParts = mentionData.pageUrl.split('/').filter(Boolean);
        if (pathParts.length > 0) {
          domainName = pathParts[0].charAt(0).toUpperCase() + pathParts[0].slice(1);
        }
      } else if (entryDisplayName) {
        // Fallback to first part of entryDisplayName
        const parts = entryDisplayName.split(' - ');
        if (parts.length > 0) {
          domainName = parts[0];
        }
      }

      // Clean the message text (remove mention tags like @[Name](email))
      let cleanedMessage = mentionData.messageText;
      
      // Remove mention tags like @[Name](email) but keep the name
      cleanedMessage = cleanedMessage.replace(/@\[([^\]]+)\]\([^)]+\)/g, '');
      
      // Remove any leading/trailing quotes or whitespace
      cleanedMessage = cleanedMessage.replace(/^["']|["']$/g, '').trim();
      
      // Truncate to ~100 characters with ellipsis for Slack
      const maxLength = 100;
      if (cleanedMessage.length > maxLength) {
        cleanedMessage = cleanedMessage.substring(0, maxLength).trim() + '...';
      }

      // Construct the notification URL with deep linking
      // Use environment variable or default to production URL
      const baseUrl = typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : process.env.VITE_APP_URL || 'https://app.thriveos.com';
      
      let notificationUrl = baseUrl;
      
      if (mentionData.pageUrl) {
        // Use the pageUrl for deep linking to the exact comment location
        notificationUrl = `${baseUrl}${mentionData.pageUrl}`;
        
        // Add scroll position and pin ID as query parameters for precise navigation
        const params = new URLSearchParams();
        if (mentionData.scrollY !== undefined) {
          params.append('scrollY', mentionData.scrollY.toString());
        }
        if (mentionData.pinId) {
          params.append('pinId', mentionData.pinId);
        }
        if (params.toString()) {
          notificationUrl += `?${params.toString()}`;
        }
      }
      

      // Send Slack message with format matching in-app notification
      // Format: "{Mentioner} mentioned you in {Domain}"
      const functions = getFunctions(app, 'us-west1');
      const sendSlackMessage = httpsCallable(functions, 'sendSlackMessage');

      await sendSlackMessage({
        slackUserId,
        message: `📬 *You have a notification in ThriveOS*\n\n${mentionerName} mentioned you in ${domainName}\n\n"${cleanedMessage}"\n\n<${notificationUrl}|View comment>`,
      });


    } catch (error) {
      log.error(`[NOTIFICATION-SERVICE] Error sending Slack notification:`, error);
      // Don't throw - this is a non-critical operation
    }
  }

  /**
   * Mark a notification as read
   */
  static async markAsRead(notificationId: string): Promise<void> {
    try {
      
      const notificationRef = doc(db, this.NOTIFICATIONS_COLLECTION, notificationId);
      await updateDoc(notificationRef, {
        status: 'read',
        updatedAt: serverTimestamp(),
      });

    } catch (error) {
      log.error('Error marking notification as read:', error);
      throw error;
    }
  }

  /**
   * Mark a notification as unread
   */
  static async markAsUnread(notificationId: string): Promise<void> {
    try {
      
      const notificationRef = doc(db, this.NOTIFICATIONS_COLLECTION, notificationId);
      await updateDoc(notificationRef, {
        status: 'unread',
        updatedAt: serverTimestamp(),
      });

    } catch (error) {
      log.error('Error marking notification as unread:', error);
      throw error;
    }
  }

  /**
   * Dismiss/delete a notification
   */
  static async dismissNotification(notificationId: string): Promise<void> {
    try {
      
      const notificationRef = doc(db, this.NOTIFICATIONS_COLLECTION, notificationId);
      
      // First try to mark as dismissed (soft delete)
      try {
        await updateDoc(notificationRef, {
          status: 'dismissed',
          updatedAt: serverTimestamp(),
        });
      } catch (updateError) {
        // If update fails, try hard delete
        log.warn(`[NOTIFICATION-SERVICE] Soft dismiss failed, attempting hard delete: ${notificationId}`);
        await deleteDoc(notificationRef);
      }

    } catch (error) {
      log.error('Error dismissing notification:', error);
      throw error;
    }
  }

  /**
   * Get user's notifications with pagination
   */
  static async getUserNotifications(
    recipientId: string,
    options: NotificationPaginationOptions = {}
  ): Promise<NotificationDocument[]> {
    try {
      const { limitCount = 50, lastDoc } = options;
      
      
      let q = query(
        collection(db, this.NOTIFICATIONS_COLLECTION),
        where('recipientId', '==', recipientId),
        where('status', 'in', ['unread', 'read']), // Exclude dismissed
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      // Add pagination cursor if provided
      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const snapshot = await getDocs(q);
      const notifications: NotificationDocument[] = [];

      snapshot.forEach((doc) => {
        const data = doc.data() as NotificationDocument;
        notifications.push({
          id: doc.id,
          ...data,
        });
      });

      return notifications;

    } catch (error) {
      log.error('Error fetching user notifications:', error);
      throw error;
    }
  }

  /**
   * Subscribe to real-time user notifications
   */
  static subscribeToUserNotifications(
    recipientId: string,
    callback: (notifications: NotificationDocument[]) => void,
    limitCount: number = 50
  ): Unsubscribe {
    
    const q = query(
      collection(db, this.NOTIFICATIONS_COLLECTION),
      where('recipientId', '==', recipientId),
      where('status', 'in', ['unread', 'read']), // Exclude dismissed
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    return onSnapshot(q, (snapshot) => {
      const notifications: NotificationDocument[] = [];

      snapshot.forEach((doc) => {
        const data = doc.data() as NotificationDocument;
        notifications.push({
          id: doc.id,
          ...data,
        });
      });

      callback(notifications);
    }, (error) => {
      log.error('Error in notifications subscription:', error);
    });
  }

  /**
   * Get count of unread notifications for user
   */
  static async getUnreadCount(recipientId: string): Promise<number> {
    try {
      
      const q = query(
        collection(db, this.NOTIFICATIONS_COLLECTION),
        where('recipientId', '==', recipientId),
        where('status', '==', 'unread')
      );

      const snapshot = await getDocs(q);
      const count = snapshot.size;
      
      return count;

    } catch (error) {
      log.error('Error getting unread count:', error);
      throw error;
    }
  }

  /**
   * Subscribe to real-time unread count updates
   */
  static subscribeToUnreadCount(
    recipientId: string,
    callback: (count: number) => void
  ): Unsubscribe {
    
    const q = query(
      collection(db, this.NOTIFICATIONS_COLLECTION),
      where('recipientId', '==', recipientId),
      where('status', '==', 'unread')
    );

    return onSnapshot(q, (snapshot) => {
      const count = snapshot.size;
      callback(count);
    }, (error) => {
      log.error('Error in unread count subscription:', error);
    });
  }

  /**
   * Mark all notifications as read for a user
   */
  static async markAllAsRead(recipientId: string): Promise<void> {
    try {
      
      const q = query(
        collection(db, this.NOTIFICATIONS_COLLECTION),
        where('recipientId', '==', recipientId),
        where('status', '==', 'unread')
      );

      const snapshot = await getDocs(q);
      const batch: Promise<void>[] = [];

      snapshot.forEach((doc) => {
        batch.push(
          updateDoc(doc.ref, {
            status: 'read',
            updatedAt: serverTimestamp(),
          })
        );
      });

      await Promise.all(batch);

    } catch (error) {
      log.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  /**
   * Parse message text for mentions and return mentioned user IDs
   * Supports both old @[Full Name](email) and new @username patterns
   */
  static parseMentions(messageText: string): string[] {
    const mentions: string[] = [];
    
    // ✅ BACKWARDS COMPATIBILITY: Pattern for @[Full Name](email) format
    const oldMentionPattern = /@\[([^\]]+)\]\(([^)]+)\)/g;
    let match;

    while ((match = oldMentionPattern.exec(messageText)) !== null) {
      const name = match[1]; // Full Name from @[Full Name](email)
      const email = match[2]; // Email from @[Full Name](email)
      
      if (name && email) {
        // Store both name and email for user resolution
        mentions.push(name.trim());
        mentions.push(email.trim());
      }
    }

    // ✅ NEW FORMAT: Pattern for simplified @username format
    // Match @FirstName LastName (capitalized words) and stop naturally at lowercase words or punctuation
    // Fixed regex: matches one or more capitalized words and stops naturally
    const newMentionPattern = /@([A-Z][a-z]*(?:\s+[A-Z][a-z]*)*)/g;
    const newMatches = [...messageText.matchAll(newMentionPattern)];
    
    for (const newMatch of newMatches) {
      const username = newMatch[1].trim();
      if (username) {
        // For new format, we need to be more precise about name matching
        // Only add names that look like actual user names (not random text)
        if (username.length > 1 && !username.match(/^\d+$/)) {
          mentions.push(username);
        }
      }
    }

    // Remove duplicates and return
    return [...new Set(mentions)];
  }

  /**
   * Resolve mentions to system user IDs
   * Matches against employee_data.name field and employee_id
   */
  static async resolveMentionsToUserIds(mentions: string[]): Promise<string[]> {
    if (mentions.length === 0) return [];

    try {
      
      const userIds: string[] = [];
      
      for (const mention of mentions) {
        // Query system_users collection for matches
        const systemUsersRef = collection(db, COLLECTION_NAMES.APP_USERS);
        
        // Try matching by name first (employee_data.name)
        const nameQuery = query(
          systemUsersRef,
          where('employee_data.name', '==', mention)
        );
        
        const nameSnapshot = await getDocs(nameQuery);
        
        if (!nameSnapshot.empty) {
          nameSnapshot.forEach((doc) => {
            userIds.push(doc.id);
          });
          continue;
        }

        // Try matching by email (direct email field)
        const emailQuery = query(
          systemUsersRef,
          where('email', '==', mention)
        );
        
        const emailSnapshot = await getDocs(emailQuery);
        
        if (!emailSnapshot.empty) {
          emailSnapshot.forEach((doc) => {
            userIds.push(doc.id);
          });
          continue;
        }

        // Try matching by employee_id if it's a number
        const employeeId = parseInt(mention);
        if (!isNaN(employeeId)) {
          const idQuery = query(
            systemUsersRef,
            where('employee_id', '==', employeeId)
          );
          
          const idSnapshot = await getDocs(idQuery);
          
          if (!idSnapshot.empty) {
            idSnapshot.forEach((doc) => {
              userIds.push(doc.id);
            });
          }
        }
      }

      return Array.from(new Set(userIds)); // Remove duplicates

    } catch (error) {
      log.error('Error resolving mentions to user IDs:', error);
      return [];
    }
  }

  /**
   * Truncate text to specified length with ellipsis
   */
  private static truncateText(text: string, maxLength: number): string {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + '...';
  }

  /**
   * Get notification by ID
   */
  static async getNotificationById(notificationId: string): Promise<NotificationDocument | null> {
    try {
      const notificationRef = doc(db, this.NOTIFICATIONS_COLLECTION, notificationId);
      const notificationDoc = await getDoc(notificationRef);
      
      if (!notificationDoc.exists()) {
        return null;
      }

      return {
        id: notificationDoc.id,
        ...notificationDoc.data()
      } as NotificationDocument;

    } catch (error) {
      log.error('Error getting notification by ID:', error);
      throw error;
    }
  }

  /**
   * Batch create notifications for multiple recipients
   * Useful for system-wide notifications or bulk mentions
   */
  static async createBulkNotifications(
    recipientIds: string[],
    notificationData: Omit<NotificationDocument, 'id' | 'recipientId' | 'createdAt' | 'updatedAt'>
  ): Promise<void> {
    try {
      
      const batch = recipientIds.map(recipientId => {
        const notificationDoc: Omit<NotificationDocument, 'id'> = {
          ...notificationData,
          recipientId,
          createdAt: serverTimestamp() as Timestamp,
          updatedAt: serverTimestamp() as Timestamp,
        };
        
        return addDoc(collection(db, this.NOTIFICATIONS_COLLECTION), notificationDoc);
      });

      await Promise.all(batch);

    } catch (error) {
      log.error('Error creating bulk notifications:', error);
      throw error;
    }
  }

  /**
   * Process mention notification from MentionData interface
   * This method handles the complete flow: parse mentions, resolve users, create notifications
   */
  static async processMentionNotification(mentionData: MentionData): Promise<void> {
    try {
      
      // Parse mentions from message text
      const mentions = this.parseMentions(mentionData.messageText);
      
      if (mentions.length === 0) {
        return;
      }
      
      // Resolve mentions to user IDs
      const userIds = await this.resolveMentionsToUserIds(mentions);
      
      if (userIds.length === 0) {
        return;
      }
      
      // Create notifications for each mentioned user
      const notificationPromises = userIds.map(userId =>
        this.createMentionNotification(userId, mentionData)
      );
      
      await Promise.all(notificationPromises);

    } catch (error) {
      log.error('Error creating mention notification from MentionData:', error);
      throw error;
    }
  }
}
